/**
 * `loadDocView(key, ctx)` — the demo's `docView(key)` (demo line 2418).
 *
 * One function, seventeen branches, in the demo's own source order. Given a
 * document key it returns a typed view model — a title, a path, a metadata
 * list and an ordered list of blocks — which `DocViewer.tsx` renders as JSX.
 *
 * ## Why this is async, and why that is the point
 *
 * The demo had the whole 6.7 MB data pack in memory, so `docView` was
 * synchronous. The port splits that pack into twelve lazy domains and 271
 * individual lake files, and a data browser that eagerly pulled all of them to
 * render a tree would fetch 5.9 MB to show a list of file names.
 *
 * So the tree needs three small domains and the lake *index*; a document
 * fetches what it alone needs, when it is opened, through `loadOemDomain` and
 * `loadLakeDoc`. Both go through `useOemData`'s module-level promise cache, so
 * the second visit to a document is free and a domain another page already
 * pulled is never fetched twice.
 *
 * ## No HTML
 *
 * The demo's branches return HTML strings. Every one of them here returns
 * structured blocks instead — `{ kind: 'table' | 'text' | 'markdown' | … }` —
 * because `dangerouslySetInnerHTML` is what `npm run check:oem-handlers`
 * exists to prevent, and because a typed block can be rendered correctly in
 * both themes while a baked HTML string cannot.
 *
 * Transformation layer (layer 3) with one allowance: it fetches. That is the
 * explicit deviation — the alternative is a synchronous resolver that can only
 * work if the scene has already pulled every domain, which is the cost this
 * design exists to avoid.
 *
 * @see src/versions/v1/modules/integration/DocViewer.tsx — the renderer
 * @see src/versions/v1/modules/integration/DataBrowserScene.transform.ts — the tree
 * @see docs/logic/integration/data.md
 */

import {
  COST_PER_MACHINE,
  ENERGY_FACTORS,
  FLEET,
  HEALTH,
  IDS,
} from '../../data/core';
import type { SourceTablePayload } from '../../chrome-oem/SourceDrawerBody';
import type { LakeCell, LakeDocument } from '../../schema/document';
import { unpackTable } from '../../shared/columnar';
import { groupNum } from '../../shared/oem-engine';
import { loadLakeDoc, loadOemDomain, type WorkOrder } from '../../shared/useOemData';
import { lakeEntryFor, type TreeCtx } from './DataBrowserScene.transform';

// ---------------------------------------------------------------------------
// The view model
// ---------------------------------------------------------------------------

/** One `<dt>/<dd>` pair in the demo's `.kv` metadata list. */
export interface DocMetaRow {
  readonly label: string;
  /** Rendered in the mono face when the demo rendered it that way. */
  readonly mono?: boolean;
  readonly value: string;
}

/** A table block — the demo's `.twrap > table.t`. */
export interface DocTableBlock {
  readonly kind: 'table';
  readonly eyebrow: string;
  readonly cols: readonly string[];
  readonly rows: readonly (readonly LakeCell[])[];
  /** `'Showing 40 of 3,920 rows…'`, when the table was truncated. */
  readonly note?: string;
  /**
   * The `SRC` key the preview was cut from — set when the whole table can be
   * opened in the source drawer (demo line 2424, "open full table").
   */
  readonly dataset?: string;
}

/** Pre-formatted source text — the demo's `mono(x)`. */
export interface DocCodeBlock {
  readonly kind: 'code';
  readonly eyebrow: string;
  readonly text: string;
}

/** Prose the demo rendered in its `.ex` panel. */
export interface DocTextBlock {
  readonly kind: 'text';
  readonly eyebrow: string;
  readonly text: string;
}

/** A markdown body — the lake's knowledge articles. */
export interface DocMarkdownBlock {
  readonly kind: 'markdown';
  readonly eyebrow: string;
  readonly source: string;
}

/** A page-one thumbnail: bare base64 WebP, no data-URI prefix. */
export interface DocThumbBlock {
  readonly kind: 'thumb';
  readonly eyebrow: string;
  readonly base64: string;
}

/** The demo's "documents are built on request" panel. */
export interface DocNoticeBlock {
  readonly kind: 'notice';
  readonly eyebrow: string;
  readonly text: string;
  /** The question the Ask button sends, when there is one. */
  readonly ask?: string;
}

export type DocBlock =
  | DocTableBlock
  | DocCodeBlock
  | DocTextBlock
  | DocMarkdownBlock
  | DocThumbBlock
  | DocNoticeBlock;

/** What the viewer renders. The demo's `{title, fn, html}`, typed. */
export interface DocView {
  readonly key: string;
  readonly title: string;
  /** The demo's `fn` — the file's path in the store. */
  readonly path: string;
  readonly meta: readonly DocMetaRow[];
  readonly blocks: readonly DocBlock[];
}

// ---------------------------------------------------------------------------
// The `tab:` dataset registry
// ---------------------------------------------------------------------------

/** One entry of the demo's `SRC` map (demo line 1875). */
interface DatasetSpec {
  readonly title: string;
  readonly path: string;
  readonly cols: readonly string[];
  /** Fetches the dataset. Eager datasets resolve immediately. */
  readonly rows: () => Promise<readonly Record<string, unknown>[]>;
}

const eager = (rows: readonly unknown[]) => () =>
  Promise.resolve(rows as readonly Record<string, unknown>[]);

/**
 * Demo line 1875 — `SRC`, restricted to the twelve datasets `DOCTREE` names
 * plus `wos`, which is the document the browser opens on.
 *
 * Column lists are the demo's, verbatim; they set both the order and which
 * fields are shown at all.
 */
const DATASETS: Readonly<Record<string, DatasetSpec>> = {
  wos: {
    title: 'Work orders',
    path: '02_sap_pm/work_orders.csv',
    cols: [
      'wo_id', 'mine', 'machine_id', 'wo_type', 'pm_code', 'status', 'created',
      'component', 'downtime_h', 'labour_h', 'crew', 'labour_cost_aud',
      'parts_cost_aud', 'freight_cost_aud', 'external_cost_aud',
      'downtime_cost_aud', 'total_direct_cost_aud', 'root_cause',
    ],
    rows: async () => unpackTable<WorkOrder>((await loadOemDomain('work-orders')).wos) as unknown as Record<string, unknown>[],
  },
  fleet: {
    title: 'Fleet register',
    path: '00_fleet/fleet_register.csv',
    cols: [
      'machine_id', 'mine', 'model', 'division', 'subclass', 'drive',
      'operating_mode', 'serial', 'commissioned', 'engine_hours', 'list_price_aud',
    ],
    rows: eager(FLEET),
  },
  health: {
    title: 'Component condition register',
    path: '00_fleet/component_health.csv',
    cols: ['machine_id', 'component', 'health_score', 'status', 'hours_since_change', 'note'],
    rows: eager(HEALTH),
  },
  cpm: {
    title: 'Cost per operating hour',
    path: '09_costs/cost_per_machine.csv',
    cols: ['machine_id', 'amount_aud', 'op_hours_18m', 'cost_per_op_hour_aud'],
    rows: eager(COST_PER_MACHINE),
  },
  efac: {
    title: 'Energy factors',
    path: '09_costs/energy_factors.csv',
    cols: [
      'mine', 'power_source', 'grid_kgco2_per_kwh', 'diesel_kgco2_per_l',
      'diesel_aud_per_l', 'power_aud_per_kwh',
    ],
    rows: eager(ENERGY_FACTORS),
  },
  energy: {
    title: 'Energy and emissions · 12 days',
    path: '09_costs/energy_by_machine_12d.csv',
    cols: [
      'mine', 'machine_id', 'model', 'drive', 'hours', 'diesel_l', 'energy_kwh',
      'co2_kg', 'co2_kg_per_hour', 'energy_cost_aud',
    ],
    rows: async () => (await loadOemDomain('telemetry')).energy as unknown as Record<string, unknown>[],
  },
  shift: {
    title: 'Shift records (daily rollup)',
    path: '01_telematics/shift_end/shift_records_all_mines.csv',
    cols: ['machine_id', 'date', 'hours', 'alarms', 'abuse', 'diesel_l', 'kwh', 'co2_kg', 'delays'],
    rows: async () => unpackTable((await loadOemDomain('telemetry')).shift_daily) as unknown as Record<string, unknown>[],
  },
  metric: {
    title: 'Daily key metric',
    path: '01_telematics/daily_key_metric_all_machines.csv',
    cols: ['machine_id', 'date', 'metric', 'value', 'utilisation_pct'],
    rows: async () => unpackTable((await loadOemDomain('telemetry')).daily_metric) as unknown as Record<string, unknown>[],
  },
  events: {
    title: 'Fault / alarm events (daily counts)',
    path: '01_telematics/fault_events.csv',
    cols: ['machine_id', 'day', 'code', 'severity', 'n'],
    rows: async () => unpackTable((await loadOemDomain('telemetry')).events_daily) as unknown as Record<string, unknown>[],
  },
  inv: {
    title: 'Inventory snapshot',
    path: '03_parts_salesforce/inventory_snapshot.csv',
    cols: ['location', 'part_no', 'qty_on_hand', 'min_level'],
    rows: async () => (await loadOemDomain('parts')).inventory as unknown as Record<string, unknown>[],
  },
  parts: {
    title: 'Parts catalogue',
    path: '03_parts_salesforce/parts_catalogue.csv',
    cols: ['part_no', 'description', 'fits', 'system', 'list_price_aud', 'lead_time_days', 'criticality'],
    rows: async () => (await loadOemDomain('parts')).parts as unknown as Record<string, unknown>[],
  },
  cases: {
    title: 'Service cases',
    path: '03_parts_salesforce/service_cases.csv',
    cols: ['case_no', 'opened', 'mine', 'machine_id', 'type', 'status', 'owner', 'subject'],
    rows: async () => (await loadOemDomain('service')).cases as unknown as Record<string, unknown>[],
  },
  ledger_c: {
    title: 'Cost ledger · by crew and category',
    path: '09_costs/cost_by_crew.csv',
    cols: ['mine', 'crew', 'category', 'amount_aud'],
    rows: async () => (await loadOemDomain('costs')).cost_by_crew_cat as unknown as Record<string, unknown>[],
  },
};

/** Demo line 2422 — the table preview is the first forty rows. */
const PREVIEW_ROWS = 40;

// ---------------------------------------------------------------------------
// Metadata helper
// ---------------------------------------------------------------------------

/** Demo line 2419 — `meta(type, src, date, author, extra)`. */
const meta = (
  type: string,
  source: string,
  date: string,
  author: string,
  extra: readonly DocMetaRow[] = [],
): DocMetaRow[] => [
  { label: 'Type', value: type },
  { label: 'Source', value: source },
  { label: 'Date', value: date, mono: true },
  { label: 'Author', value: author },
  ...extra,
];

// ---------------------------------------------------------------------------
// The resolver
// ---------------------------------------------------------------------------

/**
 * Open one document.
 *
 * @param key - a canonical key from `resolveDocKey`.
 * @returns the view model, or `null` when nothing in the pack matches — which
 *   the scene reports as the demo's "no preview built" message rather than as
 *   a blank pane.
 */
export async function loadDocView(key: string, ctx: TreeCtx): Promise<DocView | null> {
  const colon = key.indexOf(':');
  const kind = colon > 0 ? key.slice(0, colon) : key;
  const name = colon > 0 ? key.slice(colon + 1) : '';

  switch (kind) {
    case 'tab':
      return datasetView(key, name);
    case 'wo':
      return workOrderCsvView(key);
    case 'sap':
      return sapExportView(key, name);
    case 'doc':
      return libraryView(key, name, ctx);
    case 'eml':
      return emailView(key, name, ctx);
    case 'mtg':
      return meetingView(key, name, ctx);
    case 'vtt':
      return videoView(key, name, ctx);
    case 'rep':
      return reportView(key, name);
    case 'x':
      return lakeView(key, name, ctx);
    case 'mqtt':
    case 'can':
    case 'xml':
    case 'plan':
    case 'py':
      return rawSampleView(key, kind, name);
    default:
      return null;
  }
}

// ---- tables --------------------------------------------------------------

async function datasetView(key: string, dataset: string): Promise<DocView | null> {
  const spec = DATASETS[dataset];
  if (!spec) return null;
  const rows = await spec.rows();
  const shown = rows.slice(0, PREVIEW_ROWS);

  return {
    key,
    title: spec.title,
    path: spec.path,
    meta: meta(
      `Table · ${groupNum(rows.length)} rows`,
      'Connector import',
      '2026-09-09',
      'AiRis ProjeX OEM+',
    ),
    blocks: [
      {
        kind: 'table',
        eyebrow: `Preview · first ${Math.min(PREVIEW_ROWS, rows.length)} rows`,
        cols: spec.cols,
        rows: shown.map((row) => spec.cols.map((c) => normaliseCell(row[c]))),
        note:
          rows.length > shown.length
            ? `Showing ${shown.length} of ${groupNum(rows.length)} rows — open the full table to search the rest.`
            : undefined,
        dataset,
      },
    ],
  };
}

/** The dataset behind a view's table preview, when it can be opened in full. */
export function fullTableDataset(view: DocView | null): string | null {
  for (const block of view?.blocks ?? []) {
    if (block.kind === 'table' && block.dataset) return block.dataset;
  }
  return null;
}

/**
 * Demo line 2424 — `audit(fn, {})`, the preview's "open full table" link: the
 * whole dataset as a source-drawer payload. The drawer renders the first 400
 * rows and its "Filter rows…" box searches all of them.
 *
 * @returns `null` for a key that is not one of the browser's datasets.
 */
export async function loadFullTable(dataset: string): Promise<SourceTablePayload | null> {
  const spec = DATASETS[dataset];
  if (!spec) return null;
  const rows = await spec.rows();
  const fmt = spec.path.split('.').pop()?.toUpperCase() ?? '';
  return {
    kind: 'table',
    title: spec.title,
    columns: [...spec.cols],
    rows: rows.map((row) =>
      spec.cols.map((c) => {
        const cell = normaliseCell(row[c]);
        return typeof cell === 'boolean' ? String(cell) : cell;
      }),
    ),
    provenance: `meridian-data/${spec.path} · ${fmt} · ${groupNum(rows.length)} of ${groupNum(rows.length)} rows`,
  };
}

/** Demo line 2450 — the raw CSV head of the SAP PM work-order export. */
async function workOrderCsvView(key: string): Promise<DocView> {
  const rows = unpackTable<WorkOrder>((await loadOemDomain('work-orders')).wos);
  const header =
    'wo_id,mine,machine_id,model,wo_type,priority,status,created,downtime_h,component,crew,total_direct_cost_aud,downtime_cost_aud';
  const lines = rows
    .slice(0, 14)
    .map((w) =>
      [
        w.wo_id, w.mine, w.machine_id, w.model, w.wo_type, w.priority, w.status,
        w.created, w.downtime_h, w.component, w.crew, w.total_direct_cost_aud,
        w.downtime_cost_aud,
      ].join(','),
    );

  return {
    key,
    title: 'Work orders · SAP PM export',
    path: 'work_orders.csv',
    meta: meta(`CSV · ${groupNum(rows.length)} rows`, 'SAP PM nightly export', '2026-09-09 02:10', 'SAP PM'),
    blocks: [{ kind: 'code', eyebrow: 'Content', text: [header, ...lines].join('\n') }],
  };
}

/** Demo line 2451 — the IDoc-shaped JSON the SAP connector exports. */
async function sapExportView(key: string, name: string): Promise<DocView> {
  const rows = unpackTable<WorkOrder>((await loadOemDomain('work-orders')).wos);
  const plant: Record<string, string> = { KG: '1100', BR: '1200', MI: '1300', OH: '1400', GD: '1500' };
  const type: Record<string, string> = {
    Preventive: 'PM01', Corrective: 'PM02', Breakdown: 'PM03', Predictive: 'PM04',
  };
  const payload = {
    export: {
      system: 'SAP PM',
      client: 'PRD/100',
      plant_map: plant,
      orders: rows.slice(0, 3).map((w) => ({
        AUFNR: w.wo_id,
        IWERK: plant[w.mine],
        EQUNR: w.machine_id,
        AUART: type[w.wo_type],
        KTEXT: (w.description || w.pm_code || '').slice(0, 40),
        COST: w.total_direct_cost_aud,
      })),
    },
  };

  return {
    key,
    title: 'SAP PM orders export (IDoc-shaped JSON)',
    path: name,
    meta: meta('JSON', 'SAP PM', '2026-09-09', 'PRD/100'),
    blocks: [{ kind: 'code', eyebrow: 'Content', text: JSON.stringify(payload, null, 1) }],
  };
}

// ---- documents, mail, meetings, video ------------------------------------

/** Demo line 2420 — a controlled document out of the SharePoint library. */
function libraryView(key: string, file: string, ctx: TreeCtx): DocView | null {
  const doc = ctx.docIndex.docs.find((d) => d.file === file || d.file.startsWith(file));
  if (!doc) return null;

  const id = doc.file.split('_')[0];
  const rev = doc.file.match(/_Rev([A-Z0-9-]+)_/)?.[1] ?? '';
  const date = doc.file.match(/20\d\d-\d\d-\d\d/)?.[0] ?? '2026';

  return {
    key,
    title: doc.title,
    path: doc.file,
    meta: meta(
      'Word document',
      'SharePoint · OEM technical library',
      date,
      /^MI-/.test(id)
        ? 'Mount Isolde Copper · Geotechnical'
        : /^LOTO/.test(id)
          ? 'Site safety (Meridian-adopted)'
          : 'Meridian Reliability Engineering',
      [
        { label: 'Document', value: documentType(id) },
        { label: 'Reference', value: id + (rev ? ` Rev ${rev}` : ''), mono: true },
        { label: 'Classification', value: classification(id) },
      ],
    ),
    blocks: [{ kind: 'text', eyebrow: 'Excerpt', text: doc.excerpt }],
  };
}

/** Demo line 2420 — the document-type ladder, in its original order. */
function documentType(id: string): string {
  if (/^SP-/.test(id)) return 'Service procedure';
  if (/^TB-/.test(id)) return 'Technical bulletin';
  if (/^PM-/.test(id)) return 'Preventive maintenance standard';
  if (/^LOTO/.test(id)) return 'Isolation standard (site-adopted)';
  if (/^MI-/.test(id)) return 'Site protocol';
  if (/^WO-/.test(id)) return 'Breakdown report';
  if (/^IR-/.test(id)) return 'Incident report';
  return 'Technical document';
}

/** Demo line 2420 — the classification line on the letterhead. */
function classification(id: string): string {
  if (/^(WO|IR)-/.test(id)) return 'Internal — customer copy issued';
  if (/^(LOTO|MI-)/.test(id)) return 'Site controlled document';
  return 'Internal — customer shareable';
}

/** Demo line 2421 — one message out of the Outlook connector. */
function emailView(key: string, file: string, ctx: TreeCtx): DocView | null {
  const email = ctx.comms.emails.find((e) => e.file === file || e.file.startsWith(file));
  if (!email) return null;

  return {
    key,
    title: email.subject,
    path: email.file,
    meta: meta('Email', 'Outlook connector', email.date, `${email.from_} → ${email.to}`),
    blocks: [{ kind: 'text', eyebrow: 'Body', text: ctx.comms.email_bodies[email.file] ?? '' }],
  };
}

/** Demo line 2422 — a Teams meeting transcript. */
function meetingView(key: string, file: string, ctx: TreeCtx): DocView | null {
  const m = ctx.comms.meetings.find((x) => x.transcript === file);
  if (!m) return null;

  return {
    key,
    title: m.title,
    path: m.transcript,
    meta: meta(
      `Meeting transcript · ${m.duration_min} min`,
      'Teams connector',
      m.date,
      m.organiser,
      [{ label: 'Attendees', value: m.attendees }],
    ),
    blocks: [{ kind: 'text', eyebrow: 'Transcript', text: ctx.comms.transcripts[m.transcript] ?? '' }],
  };
}

/** Demo line 2423 — a WebVTT caption track from the video library. */
function videoView(key: string, file: string, ctx: TreeCtx): DocView {
  const v = ctx.comms.videos.find((x) => x.transcript === file);
  return {
    key,
    title: v ? v.title : file,
    path: file,
    meta: meta(
      v ? v.type : 'Transcript',
      'Video library',
      v ? v.recorded : '',
      v ? v.equipment : '',
    ),
    blocks: [{ kind: 'code', eyebrow: 'Content', text: ctx.comms.vtts[file] ?? '' }],
  };
}

// ---- generated reports ----------------------------------------------------

/**
 * Demo line 2442 — the generated-report panel.
 *
 * The demo matched on the file name to pick the question that rebuilds the
 * document. The rendered DOCX/PPTX blobs and their file names were dropped at
 * extract time (plan §A step 4), so the port keys the same five questions off
 * the report ids the `reports` domain still carries.
 */
const REPORT_PROMPT: Readonly<Record<string, string>> = {
  surface: 'Generate the weekly status report for Surface Drilling',
  drills: 'Generate the weekly status report for Underground Drilling',
  loadhaul: 'Generate the weekly status report for Load & Haul',
  email_mi: 'Write the machine risk email for Mount Isolde',
  deck_br: 'Create the Broken Ridge equipment summary presentation',
};

function reportView(key: string, id: string): DocView {
  const ask = REPORT_PROMPT[id];
  return {
    key,
    title: id === 'audit' ? 'report_figures.json' : (ask ?? id),
    path: `11_reports/${id}`,
    meta: meta(
      'Generated report',
      'Fleet workspace · assistant',
      '2026-09-09 06:05',
      'Fleet Reliability Lead (via assistant)',
    ),
    blocks: [
      {
        kind: 'notice',
        eyebrow: 'Content',
        text: ask
          ? 'Documents are built on request. Ask the assistant and it rebuilds this from the live data and offers the file.'
          : 'Every figure used by the four documents, as JSON, for audit.',
        ask,
      },
    ],
  };
}

// ---- the lake -------------------------------------------------------------

/** Demo line 2443 — one raw source file, rendered in its own format. */
async function lakeView(key: string, file: string, ctx: TreeCtx): Promise<DocView | null> {
  const entry = lakeEntryFor(file, ctx.lake);
  if (!entry) return null;

  const doc = await loadLakeDoc(entry.slug);
  const date = entry.file.match(/20\d\d-\d\d(?:-\d\d)?|W3\d/)?.[0] ?? '2026-09';

  return {
    key,
    title: entry.file.replace(/[_-]/g, ' ').replace(/\.\w+$/, ''),
    path: entry.path,
    meta: meta(
      `${formatLabel(entry.kind)} · ${(entry.size / 1024).toFixed(0)} kB`,
      entry.conn,
      date,
      entry.conn.split(' ·')[0],
      entry.machines?.length
        ? [{ label: 'Machines', value: entry.machines.join(' '), mono: true }]
        : [],
    ),
    blocks: lakeBlocks(doc),
  };
}

/** Demo line 2447 — the format label on the metadata list. */
function formatLabel(kind: string): string {
  const labels: Record<string, string> = {
    csv: 'CSV export',
    xlsx: 'Excel export',
    pdf: 'PDF',
    json: 'JSON (REST / export)',
    xml: 'XML (integration framework)',
    jsonl: 'JSON-lines stream',
    txt: 'Plain-text notes',
  };
  return labels[kind] ?? kind.toUpperCase();
}

/** Demo lines 2444–2446 — the per-format bodies, in the demo's own order. */
function lakeBlocks(doc: LakeDocument): DocBlock[] {
  if (doc.kind === 'csv' && doc.cols && doc.rows) {
    return [
      {
        kind: 'table',
        eyebrow: `Preview · ${groupNum(doc.n ?? doc.rows.length)} rows`,
        cols: doc.cols,
        rows: doc.rows,
        note:
          doc.n && doc.n > doc.rows.length
            ? `Showing ${doc.rows.length} of ${groupNum(doc.n)} rows — the full file ships in the data pack.`
            : undefined,
      },
    ];
  }

  if (doc.kind === 'xlsx' && doc.sheets) {
    return doc.sheets.map(
      (sheet): DocTableBlock => ({
        kind: 'table',
        eyebrow: `Sheet · ${sheet.name} · ${sheet.n} rows`,
        cols: sheet.cols,
        rows: sheet.rows,
        note:
          sheet.n > sheet.rows.length
            ? `Showing ${sheet.rows.length} of ${groupNum(sheet.n)} rows.`
            : undefined,
      }),
    );
  }

  if (doc.kind === 'pdf' && doc.thumb) {
    const blocks: DocBlock[] = [{ kind: 'thumb', eyebrow: 'Page 1', base64: doc.thumb }];
    if (doc.text) blocks.push({ kind: 'code', eyebrow: 'Text layer (page 1)', text: doc.text });
    return blocks;
  }

  if (doc.kind === 'md' && doc.text) {
    return [{ kind: 'markdown', eyebrow: 'Knowledge article', source: doc.text }];
  }

  if (doc.text) {
    const tail = doc.n ? `\n… ${groupNum(doc.n)} lines` : '';
    return [{ kind: 'code', eyebrow: 'Content', text: doc.text + tail }];
  }

  return [
    {
      kind: 'notice',
      eyebrow: 'Content',
      text: 'This file carries no extracted text — the source ships in the data pack.',
    },
  ];
}

// ---- the raw integration formats -----------------------------------------

/** Demo lines 2448–2453 — the five raw-format samples. */
async function rawSampleView(key: string, kind: string, name: string): Promise<DocView> {
  const live = await loadOemDomain('telemetry-live');
  const machine = name.split('_')[0];

  switch (kind) {
    case 'mqtt':
      return {
        key,
        title: `Live telematics · MQTT JSON-lines · ${machine}`,
        path: name,
        meta: meta(
          'Machine telemetry · 10 s',
          'Underground Wi-Fi/LTE → MQTT broker',
          '2026-09-08',
          'mi_live_collector.py',
        ),
        blocks: [{ kind: 'code', eyebrow: 'Content', text: live.mqtt_sample.join('\n') }],
      };
    case 'can':
      return {
        key,
        title: `CAN bus log · candump format · ${machine}`,
        path: name,
        meta: meta(
          'CAN bus capture · 10 Hz',
          'Onboard gateway (J1939-style PGNs)',
          '2026-09-09 04:38',
          'can1',
        ),
        blocks: [{ kind: 'code', eyebrow: 'Content', text: live.can_sample.join('\n') }],
      };
    case 'xml':
      return {
        key,
        title: 'Pitram shift-end export',
        path: name,
        meta: meta('FMS shift export · XML', 'Pitram', '2026-09-03 18:05', 'Pitram 4.2'),
        blocks: [{ kind: 'code', eyebrow: 'Content', text: live.xml_sample }],
      };
    case 'plan':
      return {
        key,
        title: `Drill plan · 1840XC cut 212 · ${IDS.J07 ?? 'J-07'}`,
        path: `drillplan_${IDS.J07 ?? 'J-07'}_BR_1840XC_cut_212.xml`,
        meta: meta(
          'Drill plan · IREDES-style XML',
          'Rig control system export',
          '2026-09-08 05:40',
          'Broken Ridge survey',
        ),
        blocks: [{ kind: 'code', eyebrow: 'Content', text: live.drillplan_sample }],
      };
    default:
      return {
        key,
        title: 'mi_live_collector.py',
        path: name || 'mi_live_collector.py',
        meta: meta('Python · connector script', '10_scripts', '2026-09', 'Meridian / consortium'),
        blocks: [{ kind: 'code', eyebrow: 'Content', text: live.collector_sample }],
      };
  }
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/**
 * Demo line 2422 — a non-integer number prints to one decimal in the `tab:`
 * tables (the lake tables use two, which `LakeCell` values already carry).
 */
function normaliseCell(value: unknown): LakeCell {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isInteger(value) ? value : +value.toFixed(1);
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.join(' ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
